/**
 * Material database for the Pipe Sketch tool.
 *
 * One place that holds the *product* data behind every number the tool reports:
 *   • pipe schedules — OD, wall, weight per foot, stock length, price
 *   • fittings, valves, supports and terminations — take-out, weight, price each
 *   • insulation, jacket and coating — density, thickness band, price
 *
 * Estimating, cut list and insulation takeoff all read their unit costs from
 * here instead of carrying flat numbers, so editing a catalog price re-rolls
 * every total. Seed prices ship in the bundle (offline-first); user edits are
 * stored on the device and merged in through `setCatalogOverrides`.
 */

import {
  COMPONENT_KINDS,
  SUPPORT_KINDS,
  TERMINATION_KINDS,
  getComponentSpec,
  getMaterial,
  getSizeSpec,
  getSupportSpec,
  getTerminationSpec,
  MATERIALS,
  type ComponentKind,
  type MaterialId,
  type SupportKind,
  type TerminationKind,
} from "@/lib/pipe-spec";
import {
  BW_45_LB_STD,
  BW_90_LB_STD,
  BW_90_LB_XS,
  BW_RED_LB_STD,
  BW_TEE_LB_STD,
  COATING_PRICE,
  DENSITY_FACTOR,
  FITTING_MIN_PRICE,
  FITTING_PRICE_PER_LB,
  FLANGE_PRICE_PER_LB,
  HANGER_PRICE,
  INSUL_PRICE_ADDER,
  INSUL_PRICE_COEF,
  JACKET_PRICE,
  PIPE_PRICE_PER_LB as PIPE_LB_PRICE,
  STOCK_LENGTH_FT as STOCK_LEN,
  SUPPORT_PRICE_MULT,
  VALVE_PRICE,
  bySize,
} from "@/lib/material-data";
import {
  AWG_SIZES,
  CU_WEIGHT_LB_PER_KFT,
  DEVICES,
  DEVICE_KEYS,
  RACEWAYS,
  TRADE_SIZES,
  WIRE_TYPES,
  type AwgSize,
  type InsulationId,
  type RacewayId,
  type TradeSize,
} from "@/lib/electrical-data";
import {
  PLUMBING_ITEMS,
  PLUMBING_SECTIONS,
  getPlumbingItem,
  plumbingItems,
  type PlumbingSection,
} from "@/lib/plumbing-data";
import {
  HVAC_ITEMS,
  HVAC_SECTIONS,
  getHvacItem,
  hvacItems,
  hvacUnitLabel,
  type HvacSection,
} from "@/lib/hvac-data";
import {
  STRUCTURAL_ITEMS,
  STRUCTURAL_SECTIONS,
  structuralItems,
  getStructuralItem,
  structuralUnitLabel,
  type StructuralSection,
} from "@/lib/structural-data";
import {
  GENERAL_ITEMS,
  GENERAL_SECTIONS,
  generalItems,
  getGeneralItem,
  generalUnitLabel,
  type GeneralSection,
} from "@/lib/general-data";
import {
  COATINGS,
  INSULATION_TYPES,
  recommendedThickness,
  type CoatingId,
  type InsulationType,
  type ServiceBand,
} from "@/lib/pipe-insulation";

/* ------------------------------------------------------------------ */
/* seed price data                                                     */
/* ------------------------------------------------------------------ */

/** Mill price of pipe, $ per pound (see material-data.ts for sources). */
export const PIPE_PRICE_PER_LB: Record<MaterialId, number> = PIPE_LB_PRICE as Record<
  MaterialId,
  number
>;

const STOCK_LENGTH_FT = STOCK_LEN as Record<MaterialId, number>;

export type JacketId = "none" | "asj" | "aluminum" | "stainless" | "pvc";

export type FittingKind = "elbow90" | "elbow45" | "tee" | "reducer";

export const FITTING_KINDS: FittingKind[] = ["elbow90", "elbow45", "tee", "reducer"];

const FITTING_LABEL: Record<FittingKind, string> = {
  elbow90: "90° Elbow",
  elbow45: "45° Elbow",
  tee: "Tee",
  reducer: "Concentric Reducer",
};

/** Published B16.9 weight table for each fitting, standard weight. */
const FITTING_WEIGHT_STD: Record<FittingKind, Record<number, number>> = {
  elbow90: BW_90_LB_STD,
  elbow45: BW_45_LB_STD,
  tee: BW_TEE_LB_STD,
  reducer: BW_RED_LB_STD,
};

/** Weight of one fitting, lb — real B16.9 table, walked to the actual wall. */
export function fittingWeightLb(material: MaterialId, kind: FittingKind, nps: string): number {
  const s = getSizeSpec(material, nps);
  const std = bySize(FITTING_WEIGHT_STD[kind], s.nom, 0);
  const sch40Wall = bySize(SCH40_REF_WALL, s.nom, s.wall);
  if (material === "cs-a106-sch80") {
    const xs = kind === "elbow90" ? bySize(BW_90_LB_XS, s.nom, 0) : std * 1.35;
    return round2(xs);
  }
  const wallRatio = sch40Wall > 0 ? s.wall / sch40Wall : 1;
  return round2(std * wallRatio * (DENSITY_FACTOR[material] ?? 1));
}

/** Sch 40 wall by size — the basis the published fitting weights are on. */
const SCH40_REF_WALL: Record<number, number> = {
  0.5: 0.109, 0.75: 0.113, 1: 0.133, 1.25: 0.14, 1.5: 0.145, 2: 0.154,
  2.5: 0.203, 3: 0.216, 4: 0.237, 5: 0.258, 6: 0.28, 8: 0.322, 10: 0.365,
  12: 0.406,
};

/* ------------------------------------------------------------------ */
/* override registry                                                   */
/* ------------------------------------------------------------------ */

/** Catalog price overrides, keyed by catalog row key. */
export type CatalogOverrides = Record<string, number>;

let overrides: CatalogOverrides = {};

/** Replace the active override set (called by `useMaterialCatalog`). */
export function setCatalogOverrides(next: CatalogOverrides) {
  overrides = next ?? {};
}

export function getCatalogOverrides(): CatalogOverrides {
  return overrides;
}

const priced = (key: string, seed: number) => {
  const ov = overrides[key];
  return typeof ov === "number" && isFinite(ov) && ov >= 0 ? ov : seed;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/* ------------------------------------------------------------------ */
/* catalog rows                                                        */
/* ------------------------------------------------------------------ */

export interface CatalogRow {
  key: string;
  /** product description as it reads on a purchase order */
  description: string;
  size: string;
  /** buying unit, e.g. `ft`, `ea`, `sq ft`, `gal` */
  unit: string;
  /** seed price shipped with the app */
  seedPrice: number;
  /** effective price after any device override */
  price: number;
  edited: boolean;
  /** short spec line — schedule, standard, weight, take-out */
  spec: string;
}

export const catalogKeys = {
  pipe: (m: MaterialId, nps: string) => `pipe:${m}:${nps}`,
  fitting: (m: MaterialId, k: FittingKind, nps: string) => `fit:${m}:${k}:${nps}`,
  component: (m: MaterialId, k: ComponentKind, nps: string) => `cmp:${m}:${k}:${nps}`,
  support: (k: SupportKind, nps: string) => `sup:${k}:${nps}`,
  termination: (m: MaterialId, k: TerminationKind, nps: string) => `term:${m}:${k}:${nps}`,
  insulation: (t: InsulationType, nps: string, thickness: number) =>
    `insul:${t}:${nps}:${thickness}`,
  jacket: (j: JacketId) => `jkt:${j}`,
  coating: (c: CoatingId) => `coat:${c}`,
  conduit: (r: RacewayId, size: TradeSize) => `econd:${r}:${size}`,
  wire: (i: InsulationId, awg: AwgSize) => `ewire:${i}:${awg}`,
  device: (k: string) => `edev:${k}`,
  plumbing: (k: string) => `plmb:${k}`,
  hvac: (k: string) => `hvac:${k}`,
  structural: (k: string) => `struct:${k}`,
  general: (k: string) => `gen:${k}`,
};

/* --- pipe ---------------------------------------------------------- */

export interface PipeCatalogItem extends CatalogRow {
  od: number;
  wall: number;
  lbPerFt: number;
  stockLengthFt: number;
}

export function pipeCatalogItem(material: MaterialId, nps: string): PipeCatalogItem {
  const m = getMaterial(material);
  const s = getSizeSpec(material, nps);
  const key = catalogKeys.pipe(material, nps);
  const seed = round2(s.lbPerFt * (PIPE_PRICE_PER_LB[material] ?? 1.5));
  const price = priced(key, seed);
  return {
    key,
    description: `${nps} ${m.shortLabel} pipe`,
    size: nps,
    unit: "ft",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `${m.spec} · OD ${s.od}" · wall ${s.wall}" · ${s.lbPerFt} lb/ft`,
    od: s.od,
    wall: s.wall,
    lbPerFt: s.lbPerFt,
    stockLengthFt: STOCK_LENGTH_FT[material] ?? 20,
  };
}

/** $ per foot of pipe, from the catalog. */
export const pipeCostPerFt = (material: MaterialId, nps: string) =>
  pipeCatalogItem(material, nps).price;

/* --- fittings ------------------------------------------------------ */

export function fittingCatalogItem(
  material: MaterialId,
  kind: FittingKind,
  nps: string,
): CatalogRow {
  const m = getMaterial(material);
  const s = getSizeSpec(material, nps);
  const key = catalogKeys.fitting(material, kind, nps);
  const lb = fittingWeightLb(material, kind, nps);
  const seed = round2(
    Math.max(FITTING_MIN_PRICE[material] ?? 3, lb * (FITTING_PRICE_PER_LB[material] ?? 4.2)),
  );
  const price = priced(key, seed);
  const takeout =
    kind === "elbow90"
      ? s.elbow90
      : kind === "elbow45"
        ? s.elbow45
        : kind === "tee"
          ? s.teeRun
          : 0;
  return {
    key,
    description: `${nps} ${FITTING_LABEL[kind]} — ${m.joint}`,
    size: nps,
    unit: "ea",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec:
      kind === "reducer"
        ? `${m.fittingSpec} · concentric · ${lb} lb`
        : `${m.fittingSpec} · take-out ${takeout}" · ${lb} lb`,
  };
}

export const fittingCost = (material: MaterialId, kind: string, nps: string) =>
  fittingCatalogItem(material, (FITTING_KINDS as string[]).includes(kind) ? (kind as FittingKind) : "elbow90", nps)
    .price;

/* --- valves & specialties ------------------------------------------ */

export function componentCatalogItem(
  material: MaterialId,
  kind: ComponentKind,
  nps: string,
): CatalogRow {
  const s = getSizeSpec(material, nps);
  const spec = getComponentSpec(material, kind, nps);
  const key = catalogKeys.component(material, kind, nps);
  const soft = getMaterial(material).jointType !== "bw";
  const table = VALVE_PRICE[kind];
  const seed = table
    ? round2(bySize(table, s.nom, 0) * (soft ? 0.8 : 1))
    : round2(spec.weight * FLANGE_PRICE_PER_LB + (kind === "flangePair" ? 18 : 6));
  const price = priced(key, seed);
  return {
    key,
    description: spec.label,
    size: nps,
    unit: "ea",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `${spec.note} · ${spec.length}" face-to-face · ${spec.weight} lb`,
  };
}

export const componentCost = (material: MaterialId, kind: ComponentKind, nps: string) =>
  componentCatalogItem(material, kind, nps).price;

/* --- supports ------------------------------------------------------- */

export function supportCatalogItem(
  material: MaterialId,
  kind: SupportKind,
  nps: string,
): CatalogRow {
  const s = getSizeSpec(material, nps);
  const spec = getSupportSpec(material, kind, nps);
  const key = catalogKeys.support(kind, nps);
  const seed = round2(
    bySize(HANGER_PRICE, s.nom, 4 * s.nom + 12) * (SUPPORT_PRICE_MULT[kind] ?? 1),
  );
  const price = priced(key, seed);
  return {
    key,
    description: spec.label,
    size: nps,
    unit: "ea",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `${spec.detail} · ${spec.rodSize}`,
  };
}

export const supportCost = (material: MaterialId, kind: SupportKind, nps: string) =>
  supportCatalogItem(material, kind, nps).price;

/* --- terminations ---------------------------------------------------- */

export function terminationCatalogItem(
  material: MaterialId,
  kind: TerminationKind,
  nps: string,
): CatalogRow {
  const s = getSizeSpec(material, nps);
  const spec = getTerminationSpec(material, kind, nps);
  const key = catalogKeys.termination(material, kind, nps);
  const seed =
    kind === "cap"
      ? round2(
          Math.max(
            FITTING_MIN_PRICE[material] ?? 3,
            bySize(BW_90_LB_STD, s.nom, 0) * 0.45 * (FITTING_PRICE_PER_LB[material] ?? 4.2),
          ),
        )
      : kind === "flangedEnd" || kind === "blindEnd"
        ? round2(spec.weight * FLANGE_PRICE_PER_LB + 8)
        : 0;
  const price = priced(key, seed);
  return {
    key,
    description: spec.label,
    size: nps,
    unit: "ea",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: spec.detail,
  };
}

export const terminationCost = (material: MaterialId, kind: TerminationKind, nps: string) =>
  terminationCatalogItem(material, kind, nps).price;

/* --- insulation, jacket, coating -------------------------------------- */

export function insulationCatalogItem(
  type: InsulationType,
  material: MaterialId,
  nps: string,
  thickness: number,
): CatalogRow {
  const meta = INSULATION_TYPES.find((t) => t.id === type);
  const od = getSizeSpec(material, nps).od;
  const key = catalogKeys.insulation(type, nps, thickness);
  const annulus = Math.max(0, (od + 2 * thickness) ** 2 - od ** 2) * (Math.PI / 4);
  const seed =
    type === "none" ? 0 : round2((INSUL_PRICE_COEF[type] ?? 0) * annulus + INSUL_PRICE_ADDER);
  const price = priced(key, seed);
  return {
    key,
    description: `${nps} × ${thickness}" ${meta?.label ?? type}`,
    size: nps,
    unit: "ft",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `${meta?.spec ?? "—"} · ${meta?.density ?? 0} lb/ft³`,
  };
}

export const insulationCostPerFt = (
  type: InsulationType,
  material: MaterialId,
  nps: string,
  thickness: number,
) => insulationCatalogItem(type, material, nps, thickness).price;

export function jacketCatalogItem(id: JacketId): CatalogRow {
  const key = catalogKeys.jacket(id);
  const seed = JACKET_PRICE[id] ?? 0;
  const price = priced(key, seed);
  const labels: Record<JacketId, string> = {
    none: "No jacket",
    asj: "ASJ all-service jacket",
    aluminum: 'Aluminium jacket, 0.016"',
    stainless: 'Stainless jacket, 0.010"',
    pvc: "PVC fitting covers",
  };
  return {
    key,
    description: labels[id],
    size: "—",
    unit: "sq ft",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: "Jacket / lagging material",
  };
}

export const jacketCostPerSqFt = (id: JacketId) => jacketCatalogItem(id).price;

export function coatingCatalogItem(id: CoatingId): CatalogRow {
  const key = catalogKeys.coating(id);
  const seed = COATING_PRICE[id] ?? 0;
  const price = priced(key, seed);
  const meta = COATINGS.find((c) => c.id === id);
  return {
    key,
    description: meta?.label ?? id,
    size: "—",
    unit: "gal",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: meta?.note ?? "—",
  };
}

export const coatingCostPerGal = (id: CoatingId) => coatingCatalogItem(id).price;

/* --- conduit, wire, devices (electrical) ----------------------------- */

export function conduitCatalogItem(raceway: RacewayId, size: TradeSize): CatalogRow | null {
  const r = RACEWAYS[raceway];
  const seed = r.price[size];
  const area = r.area[size];
  if (seed == null || area == null) return null;
  const key = catalogKeys.conduit(raceway, size);
  const price = priced(key, seed);
  return {
    key,
    description: `${size} ${r.label}`,
    size,
    unit: "ft",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `NEC Ch.9 Table 4 · internal area ${area} in² · ${r.hrs[size] ?? 0} hr/ft`,
  };
}

export const conduitCostPerFt = (raceway: RacewayId, size: TradeSize) =>
  conduitCatalogItem(raceway, size)?.price ?? 0;

export function wireCatalogItem(insulation: InsulationId, awg: AwgSize): CatalogRow {
  const w = WIRE_TYPES[insulation];
  const key = catalogKeys.wire(insulation, awg);
  const seed = w.price[awg];
  const price = priced(key, seed);
  return {
    key,
    description: `#${awg} ${w.label}`,
    size: `#${awg}`,
    unit: "ft",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `NEC Ch.9 Table 5 · ${w.area[awg]} in² · ${CU_WEIGHT_LB_PER_KFT[awg]} lb/1000 ft copper`,
  };
}

export const wireCostPerFt = (insulation: InsulationId, awg: AwgSize) =>
  wireCatalogItem(insulation, awg).price;

export function deviceCatalogItem(deviceKey: string): CatalogRow {
  const d = DEVICES[deviceKey];
  const key = catalogKeys.device(deviceKey);
  const seed = d?.price ?? 0;
  const price = priced(key, seed);
  return {
    key,
    description: d?.label ?? deviceKey,
    size: "—",
    unit: "ea",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `${d?.spec ?? "—"} · ${d?.hrs ?? 0} hr each`,
  };
}

export const deviceCost = (deviceKey: string) => deviceCatalogItem(deviceKey).price;

/**
 * Any electrical catalog key (conduit, wire or gear) resolved to a priced row,
 * so the job estimate can take off electrical work by key alone.
 */
export function electricalCatalogItem(key: string): CatalogRow | null {
  const parts = key.split(":");
  if (parts[0] === "econd") return conduitCatalogItem(parts[1] as RacewayId, parts[2] as TradeSize);
  if (parts[0] === "ewire") return wireCatalogItem(parts[1] as InsulationId, parts[2] as AwgSize);
  if (parts[0] === "edev") return deviceCatalogItem(parts.slice(1).join(":"));
  return null;
}

export const electricalCost = (key: string) => electricalCatalogItem(key)?.price ?? 0;

/* --- plumbing: pipe, valves, traps, fixtures -------------------------- */

export function plumbingCatalogItem(id: string): CatalogRow {
  const item = getPlumbingItem(id);
  const key = catalogKeys.plumbing(id);
  const seed = item?.price ?? 0;
  const price = priced(key, seed);
  return {
    key,
    description: item?.label ?? id,
    size: item?.size ?? "—",
    unit: item?.unit ?? "ea",
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `${item?.spec ?? "—"} · ${item?.hours ?? 0} hr/${item?.unit ?? "ea"}`,
  };
}

/** Catalog price for a plumbing item (per foot for pipe, each otherwise). */
export const plumbingCost = (id: string) => plumbingCatalogItem(id).price;

export { PLUMBING_ITEMS, PLUMBING_SECTIONS, plumbingItems, getPlumbingItem };
export type { PlumbingSection };

/* --- hvac: duct, coils, registers, blowers ---------------------------- */

export function hvacCatalogItem(id: string): CatalogRow {
  const item = getHvacItem(id);
  const key = catalogKeys.hvac(id);
  const seed = item?.price ?? 0;
  const price = priced(key, seed);
  const unit = item ? hvacUnitLabel(item.unit) : "ea";
  return {
    key,
    description: item?.label ?? id,
    size: item?.size ?? "—",
    unit,
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `${item?.spec ?? "—"} · ${item?.hours ?? 0} hr/${unit}`,
  };
}

/** Catalog price for an HVAC item (per foot / sq ft for duct, each otherwise). */
export const hvacCost = (id: string) => hvacCatalogItem(id).price;

export { HVAC_ITEMS, HVAC_SECTIONS, hvacItems, getHvacItem, hvacUnitLabel };
export type { HvacSection };

/* --- structural: concrete, rebar, steel, misc iron -------------------- */

export function structuralCatalogItem(id: string): CatalogRow {
  const item = getStructuralItem(id);
  const key = catalogKeys.structural(id);
  const seed = item?.price ?? 0;
  const price = priced(key, seed);
  const unit = item ? structuralUnitLabel(item.unit) : "ea";
  return {
    key,
    description: item?.label ?? id,
    size: item?.size ?? "—",
    unit,
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `${item?.spec ?? "—"} · ${item?.hours ?? 0} hr/${unit}`,
  };
}

/** Catalog price for a structural item (per cu yd / ft / lb / sq ft / each). */
export const structuralCost = (id: string) => structuralCatalogItem(id).price;

export {
  STRUCTURAL_ITEMS,
  STRUCTURAL_SECTIONS,
  structuralItems,
  getStructuralItem,
  structuralUnitLabel,
};
export type { StructuralSection };

/* --- general: permits, rental, site costs, consumables, travel -------- */

export function generalCatalogItem(id: string): CatalogRow {
  const item = getGeneralItem(id);
  const key = catalogKeys.general(id);
  const seed = item?.price ?? 0;
  const price = priced(key, seed);
  const unit = item ? generalUnitLabel(item.unit) : "ea";
  return {
    key,
    description: item?.label ?? id,
    size: item?.size ?? "—",
    unit,
    seedPrice: seed,
    price,
    edited: price !== seed,
    spec: `${item?.spec ?? "—"} · ${item?.hours ?? 0} hr/${unit}`,
  };
}

/** Catalog price for a general job-cost item. */
export const generalCost = (id: string) => generalCatalogItem(id).price;

export { GENERAL_ITEMS, GENERAL_SECTIONS, generalItems, getGeneralItem, generalUnitLabel };
export type { GeneralSection };

/* ------------------------------------------------------------------ */
/* browsable groups (drives the Material database panel)               */
/* ------------------------------------------------------------------ */

export type CatalogGroupId =
  | "pipe"
  | "fittings"
  | "valves"
  | "supports"
  | "insulation"
  | "conduit"
  | "wire"
  | "devices"
  | "plumbing"
  | "hvac"
  | "structural"
  | "general";

export const CATALOG_GROUPS: Array<{ id: CatalogGroupId; label: string }> = [
  { id: "pipe", label: "Pipe schedules" },
  { id: "fittings", label: "Fittings" },
  { id: "valves", label: "Valves & ends" },
  { id: "supports", label: "Supports" },
  { id: "insulation", label: "Insulation" },
  { id: "conduit", label: "Conduit" },
  { id: "wire", label: "Wire & cable" },
  { id: "devices", label: "Devices & gear" },
  { id: "plumbing", label: "Plumbing" },
  { id: "hvac", label: "HVAC" },
  { id: "structural", label: "Structural" },
  { id: "general", label: "General job costs" },
];

/** Groups that do not depend on a pipe material selection. */
export const ELECTRICAL_GROUPS: CatalogGroupId[] = ["conduit", "wire", "devices"];

/** Groups that do not depend on a pipe material selection. */
export const MATERIAL_FREE_GROUPS: CatalogGroupId[] = [...ELECTRICAL_GROUPS, "plumbing", "hvac", "structural", "general"];

/** Key prefixes per group — used to restore seed prices section by section. */
export const CATALOG_GROUP_PREFIX: Record<CatalogGroupId, string[]> = {
  pipe: ["pipe:"],
  fittings: ["fit:"],
  valves: ["cmp:", "term:"],
  supports: ["sup:"],
  insulation: ["insul:", "jkt:", "coat:"],
  conduit: ["econd:"],
  wire: ["ewire:"],
  devices: ["edev:"],
  plumbing: ["plmb:"],
  hvac: ["hvac:"],
  structural: ["struct:"],
  general: ["gen:"],
};

export const MATERIAL_LIST = Object.values(MATERIALS);

export function catalogRows(
  group: CatalogGroupId,
  material: MaterialId,
  opts?: { insulation?: InsulationType; service?: ServiceBand },
): CatalogRow[] {
  const sizes = getMaterial(material).sizes.map((s) => s.nps);
  switch (group) {
    case "pipe":
      return sizes.map((nps) => pipeCatalogItem(material, nps));
    case "fittings":
      return FITTING_KINDS.flatMap((k) => sizes.map((nps) => fittingCatalogItem(material, k, nps)));
    case "valves":
      return [
        ...COMPONENT_KINDS.flatMap((k) => sizes.map((nps) => componentCatalogItem(material, k, nps))),
        ...TERMINATION_KINDS.flatMap((k) =>
          sizes.map((nps) => terminationCatalogItem(material, k, nps)),
        ).filter((r) => r.seedPrice > 0 || r.edited),
      ];
    case "supports":
      return SUPPORT_KINDS.flatMap((k) => sizes.map((nps) => supportCatalogItem(material, k, nps)));
    case "insulation": {
      const type = opts?.insulation ?? "fiberglass";
      const service = opts?.service ?? "hotLow";
      return [
        ...sizes.map((nps) =>
          insulationCatalogItem(type, material, nps, recommendedThickness(nps, service)),
        ),
        ...(["asj", "aluminum", "stainless", "pvc"] as JacketId[]).map(jacketCatalogItem),
        ...(["primer", "primerFinish", "hightemp"] as CoatingId[]).map(coatingCatalogItem),
      ];
    }
    case "conduit":
      return (Object.keys(RACEWAYS) as RacewayId[]).flatMap((r) =>
        TRADE_SIZES.map((sz) => conduitCatalogItem(r, sz)).filter(Boolean) as CatalogRow[],
      );
    case "wire":
      return (Object.keys(WIRE_TYPES) as InsulationId[]).flatMap((i) =>
        AWG_SIZES.map((a) => wireCatalogItem(i, a)),
      );
    case "devices":
      return DEVICE_KEYS.map(deviceCatalogItem);
    case "plumbing":
      return PLUMBING_ITEMS.map((i) => plumbingCatalogItem(i.id));
    case "hvac":
      return HVAC_ITEMS.map((i) => hvacCatalogItem(i.id));
    case "structural":
      return STRUCTURAL_ITEMS.map((i) => structuralCatalogItem(i.id));
    case "general":
      return GENERAL_ITEMS.map((i) => generalCatalogItem(i.id));
    default:
      return [];
  }
}
